export type StoneMarket = 'mauritius' | 'provence';
export const STONE_MARKETS: StoneMarket[] = ['mauritius', 'provence'];
export const STONE_LOCALES = ['en', 'fr'] as const;

export function isStoneTheme(themeId?: string | null) {
  return themeId === 'stone-investment' || themeId === 'stone-investments';
}

export function stoneMarketPath(market: StoneMarket, locale: string): string {
  return locale === 'fr'
    ? `/fr/immobilier/${market === 'mauritius' ? 'ile-maurice' : 'provence'}`
    : `/en/property/${market}`;
}

export function stoneMarketLinks(market: StoneMarket) {
  return Object.fromEntries(
    STONE_LOCALES.map(locale => [locale, stoneMarketPath(market, locale)])
  );
}

/** Only the old region-only destinations move; arbitrary facets keep their policy. */
export function legacyStoneMarketPath(
  params: Record<string, string | string[] | undefined>,
  locale: string
) {
  if (Object.keys(params).length !== 1 || typeof params.region !== 'string')
    return null;
  const market = params.region.toLowerCase();
  return market === 'mauritius' || market === 'provence'
    ? stoneMarketPath(market, locale)
    : null;
}

export function getStoneMarketContent(market: StoneMarket, locale: string) {
  const french = locale === 'fr';
  if (market === 'mauritius') {
    return {
      title: french
        ? 'Immobilier de prestige à l’Île Maurice'
        : 'Luxury property in Mauritius',
      description: french
        ? 'Découvrez nos villas, appartements et programmes immobiliers à l’Île Maurice, accompagnés par les conseillers de Stone Investment.'
        : 'Explore villas, apartments and property developments in Mauritius with the local expertise of Stone Investment.',
      introduction: french
        ? 'Une villa avec jardin, un appartement près du lagon ou une résidence au sein d’un domaine : votre recherche commence par le lieu et la façon dont vous souhaitez y vivre. Parcourez notre sélection disponible à Maurice, puis échangez avec notre équipe sur les quartiers, les caractéristiques du bien et les prochaines étapes de votre acquisition.'
        : 'A villa with a garden, an apartment near the lagoon or a home within an estate: the right search starts with location and how you want to live. Browse our available Mauritius collection, then speak with our team about neighbourhoods, property features and the next steps in your purchase.',
      heading: french
        ? 'Préparer votre achat à Maurice'
        : 'Plan your Mauritius property search',
      guides: french
        ? [
            {
              href: '/fr/guides/acheter-bien-immobilier-ile-maurice-etranger',
              label: 'Acheter à Maurice en tant qu’étranger',
            },
            {
              href: '/fr/guides/villa-luxe-ile-maurice',
              label: 'Choisir une villa de luxe',
            },
            { href: '/fr/areas/tamarin', label: 'Découvrir Tamarin' },
            { href: '/fr/areas/black-river', label: 'Découvrir Rivière Noire' },
          ]
        : [
            {
              href: '/en/guides/buying-property-mauritius-foreigners',
              label: 'Buying in Mauritius as a foreigner',
            },
            {
              href: '/en/guides/luxury-villa-mauritius',
              label: 'Choosing a luxury villa',
            },
            { href: '/en/areas/tamarin', label: 'Explore Tamarin' },
            { href: '/en/areas/black-river', label: 'Explore Black River' },
          ],
    };
  }
  return {
    title: french
      ? 'Immobilier de prestige en Provence'
      : 'Luxury property in Provence',
    description: french
      ? 'Découvrez nos maisons, appartements et demeures de caractère en Provence avec les conseillers de Stone Investment.'
      : 'Discover homes, apartments and character properties in Provence with the advisers at Stone Investment.',
    introduction: french
      ? 'Un appartement à Marseille, une maison près de la Méditerranée ou une propriété de caractère dans l’arrière-pays : chaque adresse répond à un projet différent. Explorez notre sélection en Provence et dans le Sud de la France. Notre équipe vous aide à comparer les lieux, les espaces et les particularités de chaque bien avant d’organiser une visite.'
      : 'An apartment in Marseille, a home near the Mediterranean or a character property in the countryside: each address offers a different way of living. Explore our collection in Provence and the South of France. Our team helps you compare locations, spaces and the distinctive features of each property before arranging a viewing.',
    heading: french
      ? 'Préparer votre achat en Provence'
      : 'Plan your Provence property search',
    guides: french
      ? [
          {
            href: '/fr/guides/acheter-bien-immobilier-provence',
            label: 'Acheter un bien en Provence',
          },
          { href: '/fr/guides/acheter-mas-provence', label: 'Mas et bastides' },
          {
            href: '/fr/guides/chateau-sud-france',
            label: 'Châteaux et demeures historiques',
          },
          {
            href: '/fr/guides/propriete-viticole-provence',
            label: 'Propriétés viticoles',
          },
        ]
      : [
          {
            href: '/en/guides/buying-property-provence',
            label: 'Buying property in Provence',
          },
          { href: '/en/guides/mas-provence-buying', label: 'Mas and bastides' },
          {
            href: '/en/guides/chateau-south-france',
            label: 'Châteaux and historic estates',
          },
          {
            href: '/en/guides/vineyard-property-provence',
            label: 'Vineyard properties',
          },
        ],
  };
}
